import React from 'react'
import {Button, Icon, setNotification, useNavigate} from '@startlibs/components'
import {Errors, Field, validation, WithForm} from '@startlibs/form'
import {getColor, postFetcher} from '@startlibs/utils'
import {useToggle} from '@startlibs/core'
import {bearerHeader, TEXT_HTML} from '../reducers'
import {required} from '../lib/validation'
import styled from 'styled-components'
import {Card, CardHeader, SignInLayout} from '../components/SigninLayout'
import {PasswordInput} from '../components/PasswordInput'
import {FormattedMessage, useIntl} from "react-intl";
import {LocaleContext} from '../components/I18NProvider'

// max-width: 35rem;
const SignInCard = styled(Card)`
  max-width: 40rem;
  margin: auto;
  ${CardHeader} {
    text-align: center;
  }
`

const AccountEmail = styled.div `
  font-size:14px;
  font-weight: 600;
  background: rgb(240 240 240);
  padding: 0.5rem 1.25rem;
  display: inline-block;
  margin-bottom: 0.5rem;
  border-radius: 100px;
`

const ResendCodeMessage = styled.div`
  color: rgba(0,0,0,0.5);
  margin-top: 1rem;
  p {
    margin-bottom: 0.25rem;
  }
  .link {
    font-weight: 600;
  }
  .detail {
    font-size: 12px;
  }
  .loading {
    color: ${getColor('main')};
    font-weight: 600;
  }
  .success {
    color: ${getColor('success')};
  }
`

const ButtonsContainer = styled.div`
  margin-top: 2rem;
  text-align: center;
  ${Button} {
    min-width: 14rem;
  }
`
const LinkContainer = styled.div `
  margin-top: 1rem;
  text-align: center;
  color: rgba(0,0,0,0.5);
  a {
    color: inherit;
    text-decoration: underline;
  }
`
const SuccessFeedback = styled.div `
    padding: 1rem 3.5rem 1rem 1rem;
    border-radius: 6px;
    margin: 1rem auto;
    position:relative;
    text-align: left;
    background: ${getColor('success')};
    color: white;
    max-width: 35rem;
`
const CloseIcon = styled(Icon)`
  font-weight: 600;
  display: inline-block;
  text-align: center;
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  color: white;
  background-color: rgba(0, 0, 0, 0.2);
  width: 20px;
  height: 20px;
  line-height: 20px;
  &:hover {
    background: rgba(0,0,0,0.25);
  }
`


export const JwtAuthentication = ({userToggle, verify2fa, username, system, jwt, parsedJwt, autoLogin, locale, needsRegistrationm, passwordExpired}) => {
  const intl = useIntl()
  const loading = useToggle()
  const sendingRecovery = useToggle()
  const sentRecovery = useToggle()
  const navigate = useNavigate()

  const onFailure = validation.responseFailure((response) => {
    const {message, status} = response
    return (
      (status === 401) && (message === 'error.http.401') && {
        'password': [intl.formatMessage({
          defaultMessage: `Wrong password. Try again or click Forgot password to reset it.`,
          description: 'Wrong password error message'
        })]
      }
      ||
      (message === 'error.locked') && {
        'password': [intl.formatMessage({
          defaultMessage: `This account has been locked. Please check {hasEmail, select, true {<b>{email}</b>} other {your email}} and click the link to unlock.`,
          description: 'Login error message when account is locked',
        }, {
          email: response.params.email,
          hasEmail: !!response.params.email,
          b: (...chunks) => <b>{chunks}</b>
        })]
      })
      || (response.status === 403 && passwordExpired(response.params.key))
  })

  const logout = (expired) => {
    userToggle.openWith({})
    navigate(`/?systemId=${system.systemId}&username=${username}`, {state: {
        loginKey: Date.now(),
        expiredSession: expired
      }})
  }

  const onSuccess = (_, {id_token,username,idToken,preAuthToken}) => {
    if (username) {
      needsRegistration(idToken,username)
    } else if (preAuthToken) {
      verify2fa.openWith(preAuthToken)
    } else  {
      loading.open()
      window.location = system.systemWebHooks.systemAuthWebHook + id_token + "&locale="+locale
    }
  }

  const sendRecovery = (autoLogin, locale) => {
    sentRecovery.close()
    sendingRecovery.open()
    postFetcher(`/pasapi/account/reset-password/init${locale ? `?locale=${locale}` : ''}`, {username:parsedJwt.sub,systemId:system.systemId, autoLogin: autoLogin == null ? true : autoLogin})
      .then(() => {
        sentRecovery.open()
        sendingRecovery.close()
      })
      .catch(({errorResponse:{status}}) => {
        if (status===404)
          setNotification({type:'error',msg:intl.formatMessage({
              defaultMessage:'E-mail not registered yet.',
              description:'E-mail not registered yet error message'
            })})
      })
  }

  const preValidation = validation.buildValidation({
    password: [required(intl)]
  })
  const email = parsedJwt.sub
  const loginInfo = JSON.parse(parsedJwt.loginInfo)

  return (
    <SignInLayout system={system}>
      <SignInCard>
        <CardHeader>
          <h1>{system.systemName}</h1>
          <p><FormattedMessage
            defaultMessage="Enter the password linked to the account:"
            description="JWT Authentication, instructions"
          /></p>
          <AccountEmail>{email}</AccountEmail>
        </CardHeader>
        <WithForm
          alwaysSave
          preValidation={preValidation}
          onSuccess={onSuccess}
          onFailure={onFailure}
          action={({password}) =>
            postFetcher(`/pasapi/authenticate-token/?locale=${locale}`, password,bearerHeader(jwt,TEXT_HTML))
          }
        >{ form => <>
          <input type="text" name="username" value={email} autoComplete="username email" style={{'display': 'none'}} />
          <Field label={intl.formatMessage({
            defaultMessage: "Account password",
            description: "JWT Authentication, password field label"
          })}>
            {sendingRecovery.isOpen ?
              <span
                className="sending-link"
              ><FormattedMessage
                defaultMessage="Sending recovery link..."
                description="JWT Authentication, sending recovery link message"
              /></span>
              :
              <a
                className={(form.hasErrors() ? 'has-error ' : '') + 'password-link'}
                onClick={() => sendRecovery(autoLogin, locale)}
                tabIndex={2}
              ><FormattedMessage
                defaultMessage="Forgot password?"
                description="JWT Authentication, forgot password link"
              /></a>
            }
            <PasswordInput
              autoFocus
              autoComplete="current-password"
              type="password"
              tabIndex={1}
              withoutField
              placeholder={intl.formatMessage({
                defaultMessage: "Enter your password",
                description: "JWT Authentication, password input placeholder"
              })}
              path="password"
            />
          </Field>
          <Errors />
          <ButtonsContainer>
            <Button highlight isLoading={form.isLoading || loading.isOpen} tabIndex={1} type="submit"><FormattedMessage
              defaultMessage="Access"
              description="JWT Authentication, submit button"
            /></Button>
          </ButtonsContainer>
          <LinkContainer><FormattedMessage
            defaultMessage="Not your account? <a>Return</a>"
            description="JWT Authentication, not your account link"
            values={{
              a: (...chunks) => <a href={loginInfo.url}>{chunks}</a>
            }}
          /></LinkContainer>
        </>}</WithForm>
      </SignInCard>
      {sentRecovery.isOpen && <SuccessFeedback><FormattedMessage
        defaultMessage="Recovery link successfully sent to your e-mail."
        description="JWT Authentication, recovery link sent message"
      /><CloseIcon onClick={() => sentRecovery.close()} icon="x"/></SuccessFeedback>}
    </SignInLayout>
  )
}

export const JwtAuthenticationWithLocale = (props) => (
  <LocaleContext.Consumer>
    {({ locale }) => (
      <JwtAuthentication {...props} locale={locale}  />
    )}
  </LocaleContext.Consumer>
);

// export default JwtAuthenticationWithLocale;